import {
  type CalendarFilterState,
  countCalendarFilters,
  deserializeCalendarFilters,
  EMPTY_CALENDAR_FILTERS,
  serializeCalendarFilters,
} from '@opallabs/filters'
import { parseFilterHash } from './monthHash'

/**
 * Where the filters of one workspace are remembered, in the same shape the View
 * menu remembers its settings. The ids inside a filter belong to one workspace,
 * so the key does too.
 */
const storageKey = (workspaceUuid: string) =>
  `month-view-filters:${workspaceUuid}`

/**
 * Where it is remembered that this workspace's month has had filters of its
 * own at all — set by a person, or taken over from the week view.
 *
 * It cannot be answered from the filters themselves, because clearing the last
 * one forgets them: a month somebody deliberately unfiltered would then be
 * filled from the week view again on the very next load.
 */
const seededKey = (workspaceUuid: string) =>
  `month-view-filters-seeded:${workspaceUuid}`

/** The value is never read; only whether the key is there. */
const SEEDED = '1'

/**
 * The filters this workspace was last left with.
 *
 * Storage a browser refuses to hand over is a browser with nothing remembered,
 * which is the same answer as never having filtered anything.
 */
export const readStoredCalendarFilters = (
  workspaceUuid: string
): CalendarFilterState => {
  try {
    const stored = window.localStorage.getItem(storageKey(workspaceUuid))
    return stored === null
      ? EMPTY_CALENDAR_FILTERS
      : deserializeCalendarFilters(new URLSearchParams(stored))
  } catch {
    return EMPTY_CALENDAR_FILTERS
  }
}

/**
 * Remembers the filters, and forgets them when the last one is cleared. That
 * this workspace's month has been filtered at all is remembered either way.
 */
export const writeStoredCalendarFilters = (
  workspaceUuid: string,
  state: CalendarFilterState
): void => {
  try {
    const params = serializeCalendarFilters(state).toString()
    if (params === '') window.localStorage.removeItem(storageKey(workspaceUuid))
    else window.localStorage.setItem(storageKey(workspaceUuid), params)
    window.localStorage.setItem(seededKey(workspaceUuid), SEEDED)
  } catch {
    // A browser that will not remember is not a reason to stop filtering.
  }
}

/**
 * Whether this workspace's month has ever held filters of its own.
 *
 * It is what keeps an intentionally unfiltered month unfiltered: the week
 * view's filters are only taken over by a month that has never had any.
 */
export const hasOwnCalendarFilters = (workspaceUuid: string): boolean => {
  try {
    return window.localStorage.getItem(seededKey(workspaceUuid)) !== null
  } catch {
    return false
  }
}

/**
 * What the calendar opens filtered by.
 *
 * The address is asked first — the parent frame's hash, then the iframe's own
 * query — because an address is a request for a particular view, and a shared
 * link that quietly showed the reader their own filters would be no link at
 * all. Only when the address asks for nothing does the last state of this
 * workspace come back, which is what makes a filter outlive a reload.
 *
 * A person cannot see the filters restored this way in the calendar itself, so
 * the drawer counts them on the row of each category and offers one way out of
 * all of them at once.
 */
export const initialCalendarFilters = ({
  hash,
  search,
  workspaceUuid,
}: {
  hash: string
  search: string
  workspaceUuid: string
}): CalendarFilterState => {
  const asked = [parseFilterHash(hash), new URLSearchParams(search)]
    .map((params) => deserializeCalendarFilters(params))
    .find((state) => countCalendarFilters(state) > 0)

  return asked ?? readStoredCalendarFilters(workspaceUuid)
}
